// ─────────────────────────────────────────────
//  resolveSurgeon — link a sheet's surgeon cell to the facility's configured
//  surgeon (Setup → Surgeons, procedure_refs.surgeons).
//
//  THE PROBLEM: the cell is written by humans and carries a name PLUS an
//  annotation — "Ahmadian + 1" (a second surgeon on the case), "NARULA" in
//  caps, "Smith/Jones". The importer stored the whole string, so nothing linked
//  to the configured record. On 2026-08-15, 7 of 17 cases at Wesley Chapel were
//  unlinked this way, and every one of them was a surgeon who IS in Setup.
//
//  The picker compares EXACTLY (assignmentRow.cells.js: `surgeonList.includes`),
//  so "TANDON" is not "Tandon": a case-only difference is enough to leave the
//  case unlinked and render the value as a one-off orphan option.
//
//  ⚠️ IT NEVER GUESSES, AND IT NEVER MANGLES WHAT IT CANNOT ANCHOR. The name is
//  only rewritten when the base matches a configured surgeon; an unknown name is
//  returned untouched with matched:false, so a surgeon missing from Setup shows
//  up as itself and can be added, rather than being silently trimmed into
//  something that looks configured but is not.
//
//  ⚠️ THE ANNOTATION IS KEPT, NOT DISCARDED. "+ 1" means a second surgeon is in
//  the room. Dropping it to make the name match would trade a real clinical fact
//  for a tidy link. It comes back as `note` so the board can still read
//  "Ahmadian +1".
// ─────────────────────────────────────────────

// Where the name ends and the annotation begins. Deliberately narrow: a
// separator only counts when it is followed by something, so a trailing "+" or
// a hyphenated surname is left alone.
const SPLIT = /\s*([+,/])\s*(.+)$/;

const norm = (s) => String(s == null ? "" : s).trim();
const key = (s) => norm(s).toLowerCase().replace(/\s+/g, " ");

/**
 * @param {string} raw            the sheet's surgeon cell
 * @param {string[]} configured   procedure_refs.surgeons for this facility
 * @returns {{surgeon: string, note: string, matched: boolean}}
 *   surgeon — the CONFIGURED spelling when it could be linked, else `raw` as-is
 *   note    — the leftover annotation ("+1", "/ Jones"), "" when there is none
 *   matched — whether `surgeon` is now one of `configured`
 */
export function resolveSurgeon(raw, configured = []) {
  const text = norm(raw);
  if (!text) return { surgeon: "", note: "", matched: false };

  const byKey = new Map();
  for (const name of Array.isArray(configured) ? configured : []) {
    const k = key(name);
    if (k && !byKey.has(k)) byKey.set(k, norm(name));
  }

  // Whole cell matches (possibly only differing in case) — the common path.
  const whole = byKey.get(key(text));
  if (whole) return { surgeon: whole, note: "", matched: true };

  // Otherwise try the part before the first separator.
  const m = SPLIT.exec(text);
  if (m) {
    const base = text.slice(0, m.index);
    const hit = byKey.get(key(base));
    if (hit) {
      // Keep the annotation verbatim apart from tidying the spacing, so "+ 1"
      // and "+1" both read "+1" and a second name survives as "/ Jones".
      const sep = m[1];
      const rest = norm(m[2]);
      return { surgeon: hit, note: sep === "+" ? `+${rest}` : `${sep} ${rest}`, matched: true };
    }
  }

  // Nothing to anchor to: hand the cell back exactly as it arrived.
  return { surgeon: text, note: "", matched: false };
}

// Convenience for the import path: the fields to write onto an assignment row.
// `surgeonNote` is always present so a row that USED to carry one and no longer
// does gets it cleared rather than keeping a stale "+1".
export function surgeonFields(raw, configured = []) {
  const { surgeon, note } = resolveSurgeon(raw, configured);
  return { surgeon, surgeonNote: note };
}
